import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

const INK  = '#0A1F4A';
const INK2 = '#3D4F78';
const RULE = '#D6DAE4';
const ACC  = '#FF7A3D';

// TODO: update when company is incorporated and privacy contact is finalised
const PRIVACY_EMAIL = 'privacy@jyto.eu';
const COMPANY_NAME  = 'Jyto B.V.';
const COMPANY_ADDR  = '[Registered address — to be updated upon incorporation]';

const s = {
  lead:  { fontSize: 16, lineHeight: 1.75, color: INK2, margin: '0 0 40px', paddingBottom: 40, borderBottom: `1px solid ${RULE}` },
  h2:    { fontSize: 19, fontWeight: 700, color: INK, margin: '40px 0 12px', letterSpacing: '-0.02em', paddingTop: 40, borderTop: `1px solid ${RULE}` },
  h2top: { fontSize: 19, fontWeight: 700, color: INK, margin: '0 0 12px', letterSpacing: '-0.02em' },
  h3:    { fontSize: 15, fontWeight: 600, color: INK, margin: '24px 0 8px' },
  p:     { fontSize: 15, lineHeight: 1.75, color: INK2, margin: '0 0 16px' },
  ul:    { fontSize: 15, lineHeight: 1.75, color: INK2, paddingLeft: 24, margin: '0 0 16px' },
  li:    { marginBottom: 6 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '16px 0 24px', display: 'block', overflowX: 'auto' },
  th:    { textAlign: 'left', padding: '8px 12px', background: '#F6F7FB', borderBottom: `1px solid ${RULE}`, color: INK, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  td:    { padding: '10px 12px', borderBottom: `1px solid ${RULE}`, color: INK2, verticalAlign: 'top' },
  note:  { background: '#F6F7FB', border: `1px solid ${RULE}`, borderRadius: 8, padding: '16px 20px', margin: '0 0 16px', fontSize: 14, lineHeight: 1.7, color: INK2 },
  a:     { color: ACC, textDecoration: 'none', fontWeight: 500 },
};

const SUB_PROCESSORS = [
  ['Supabase Inc.',          'Primary database — account data, profiles, matches',       'US (EU hosting available)'],
  ['Render Services, Inc.',  'Backend API hosting',                                        'US'],
  ['Cloudflare, Inc.',       'Frontend hosting and CDN',                                   'US'],
  ['OpenAI LLC',             'Text embeddings for semantic matching of company profiles',  'US'],
  ['Anthropic PBC',          'AI-assisted tender translation and relevance scoring',       'US'],
  ['Pinecone Systems Inc.',  'Vector database for similarity matching',                    'US'],
  ['Stripe Inc.',            'Payment processing and subscription management',             'US'],
  ['Resend Inc.',            'Transactional email delivery',                               'US'],
];

const RETENTION = [
  ['Account and profile data',       'For the duration of your account, plus 30 days after deletion'],
  ['Match and tender data',          'For the duration of your account, plus 30 days after deletion'],
  ['Financial and billing records',  'Up to 7 years (legal requirement)'],
  ['Anonymised usage analytics',     'Indefinitely'],
];

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy policy" lastUpdated="9 May 2026">

      <p style={s.lead}>
        {COMPANY_NAME} ("<strong>Jyto</strong>", "<strong>we</strong>", "<strong>us</strong>") operates a
        B2B SaaS platform that matches EU companies with public procurement tenders from the EU's Tenders
        Electronic Daily (TED) database. This Privacy Policy explains what personal data we collect, why we
        collect it, how it is processed, and the rights you have over it.
      </p>

      <h2 style={s.h2top}>1. Data controller</h2>
      <p style={s.p}>
        The data controller for personal data processed through Jyto is:<br />
        <strong>{COMPANY_NAME}</strong><br />
        {COMPANY_ADDR}<br />
        Privacy contact:{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>
      </p>

      <h2 style={s.h2}>2. Data we collect</h2>

      <h3 style={s.h3}>Account information</h3>
      <ul style={s.ul}>
        <li style={s.li}>Email address</li>
        <li style={s.li}>Password (stored as a bcrypt hash — we never hold your plain-text password)</li>
        <li style={s.li}>Company name</li>
        <li style={s.li}>Account creation date and last login timestamp</li>
      </ul>

      <h3 style={s.h3}>Company profile information</h3>
      <p style={s.p}>
        To generate procurement matches, you create one or more company profiles. This may include:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Business description and area of expertise</li>
        <li style={s.li}>CPV (Common Procurement Vocabulary) codes representing your products or services</li>
        <li style={s.li}>Countries or regions of interest</li>
        <li style={s.li}>Company website URL</li>
      </ul>

      <h3 style={s.h3}>Subscription and billing</h3>
      <ul style={s.ul}>
        <li style={s.li}>Your active subscription plan and status (Trial, Free, Starter, Professional, or Scale)</li>
        <li style={s.li}>
          Payment card and billing details are handled entirely by Stripe — Jyto does not store
          card numbers or bank account details
        </li>
      </ul>

      <h3 style={s.h3}>Usage data</h3>
      <ul style={s.ul}>
        <li style={s.li}>Matches viewed and feedback submitted (saved, dismissed, bid tracking)</li>
        <li style={s.li}>API key usage metrics (if on an eligible plan)</li>
        <li style={s.li}>Email digest and notification preferences</li>
      </ul>

      <h2 style={s.h2}>3. How we use your data</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Providing the matching service:</strong> processing your company profile to identify relevant EU procurement tenders from TED</li>
        <li style={s.li}><strong>Email digests and alerts:</strong> sending you scheduled summaries of new relevant tenders</li>
        <li style={s.li}><strong>Subscription management:</strong> billing, plan changes, and subscription lifecycle via Stripe</li>
        <li style={s.li}><strong>Account management:</strong> authentication, session management, and customer support</li>
        <li style={s.li}><strong>Service improvement:</strong> analysing aggregated, anonymised usage patterns to improve match quality and the platform</li>
        <li style={s.li}><strong>Legal compliance:</strong> retaining records as required by applicable law</li>
      </ul>

      <h2 style={s.h2}>4. AI-assisted processing</h2>
      <p style={s.p}>
        Jyto uses AI services to power its core matching functionality. We are transparent about how your
        data interacts with these systems:
      </p>
      <div style={s.note}>
        <strong>Company profile embeddings (OpenAI):</strong> Your company profile text — business description,
        CPV codes, and related fields — is sent to OpenAI's embedding API to generate a mathematical vector
        representation. This vector is stored in Pinecone and used to find semantically similar tenders.
        We operate under a data processing agreement with OpenAI and do not permit your data to be used
        for training their models.
      </div>
      <div style={s.note}>
        <strong>Tender scoring (Anthropic Claude):</strong> Tender notices sourced from TED (public EU data)
        are processed by Anthropic's Claude AI for translation and relevance scoring. This processing
        involves public tender data. Your company profile context may inform individual match scores
        but is not stored by Anthropic beyond the scope of the API request.
      </div>
      <p style={s.p}>
        Match verdicts (Great / Good / Maybe) are automated suggestions to help you identify procurement
        opportunities. <strong>They do not constitute legal advice, procurement advice, or a guarantee of
        tender relevance or eligibility.</strong> No verdict has legal effect on your business. You remain
        solely responsible for your own procurement decisions.
      </p>

      <h2 style={s.h2}>5. Legal basis for processing (GDPR Art. 6)</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Performance of a contract (Art. 6(1)(b)):</strong> processing your account and profile data is necessary to provide the service you signed up for</li>
        <li style={s.li}><strong>Legitimate interests (Art. 6(1)(f)):</strong> fraud prevention, security monitoring, and aggregated product analytics — balanced against your rights and interests</li>
        <li style={s.li}><strong>Legal obligation (Art. 6(1)(c)):</strong> retaining financial and billing records as required by applicable law</li>
        <li style={s.li}><strong>Consent (Art. 6(1)(a)):</strong> marketing communications beyond transactional service emails, where required</li>
      </ul>

      <h2 style={s.h2}>6. Data sharing and sub-processors</h2>
      <p style={s.p}>
        We do not sell your personal data. We share data only with the sub-processors necessary to operate
        the service, listed below. All sub-processors are bound by data processing agreements.
      </p>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Processor</th>
            <th style={s.th}>Role</th>
            <th style={s.th}>Location</th>
          </tr>
        </thead>
        <tbody>
          {SUB_PROCESSORS.map(([name, role, loc]) => (
            <tr key={name}>
              <td style={s.td}><strong>{name}</strong></td>
              <td style={s.td}>{role}</td>
              <td style={s.td}>{loc}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={s.p}>
        We may also disclose personal data where required by law, a court order, or to protect the rights,
        property, or safety of Jyto, our users, or the public.
      </p>

      <h2 style={s.h2}>7. International data transfers</h2>
      <p style={s.p}>
        Several of our sub-processors are based in the United States. When personal data is transferred
        outside the European Economic Area, we rely on appropriate safeguards, including:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Standard Contractual Clauses (SCCs)</strong> approved by the European Commission</li>
        <li style={s.li}><strong>The EU-US Data Privacy Framework (DPF)</strong>, where the receiving organisation is certified under the Framework</li>
      </ul>
      <p style={s.p}>
        You may request details of the specific transfer mechanism applicable to any sub-processor by
        contacting us at <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>.
      </p>

      <h2 style={s.h2}>8. Data retention</h2>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Data type</th>
            <th style={s.th}>Retention period</th>
          </tr>
        </thead>
        <tbody>
          {RETENTION.map(([type, period]) => (
            <tr key={type}>
              <td style={s.td}>{type}</td>
              <td style={s.td}>{period}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={s.h2}>9. Your rights</h2>
      <p style={s.p}>
        Under the GDPR you have the right to access, correct, erase, or restrict the processing of your
        personal data, as well as the right to data portability and to object to certain processing.
        For a full description of each right and how to exercise them, see our{' '}
        <Link to="/gdpr" style={s.a}>GDPR &amp; data</Link> page.
      </p>

      <h2 style={s.h2}>10. Security</h2>
      <p style={s.p}>
        We implement appropriate technical and organisational measures to protect your personal data, including:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>HTTPS/TLS encryption for all data in transit</li>
        <li style={s.li}>Bcrypt hashing for all stored passwords</li>
        <li style={s.li}>JWT-based authentication with 7-day token expiry</li>
        <li style={s.li}>Role-based access controls limiting data access to authorised personnel</li>
      </ul>
      <p style={s.p}>
        No method of electronic transmission or storage is completely secure. We will notify affected users
        of any material data breach in accordance with GDPR requirements (Art. 33–34).
      </p>

      <h2 style={s.h2}>11. Children</h2>
      <p style={s.p}>
        Jyto is a business-to-business service intended for use by individuals aged 18 or over who
        are authorised to represent a company. We do not knowingly collect personal data from anyone under 18.
      </p>

      <h2 style={s.h2}>12. Changes to this policy</h2>
      <p style={s.p}>
        We may update this Privacy Policy from time to time. Material changes will be communicated by email
        or by a prominent notice within the platform at least 14 days before they take effect. Continued
        use of Jyto after the effective date of an updated policy constitutes acceptance of the changes.
      </p>

      <h2 style={s.h2}>13. Contact</h2>
      <p style={s.p}>
        For privacy-related questions or to exercise your data subject rights, contact us at{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`} style={s.a}>{PRIVACY_EMAIL}</a>.
      </p>

    </LegalPage>
  );
}
